import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Settings } from 'lucide-react';

interface ProcessorInfo {
  name: string;
  description: string;
  version: string;
  supported_formats: string[];
  parameters: Record<string, any>;
}

interface ProcessorSettingsProps {
  processor: ProcessorInfo;
  settings: Record<string, any>;
  onSettingsChange: (settings: Record<string, any>) => void;
}

export const ProcessorSettings: React.FC<ProcessorSettingsProps> = ({
  processor,
  settings,
  onSettingsChange
}) => {
  const handleSettingChange = (key: string, value: any) => {
    onSettingsChange({
      ...settings,
      [key]: value
    });
  };

  const renderParameterInput = (key: string, param: any) => {
    const value = settings[key] ?? param.default;

    switch (param.type) {
      case 'integer':
        return (
          <div key={key} className="space-y-2">
            <label className="block text-sm font-medium">
              {param.description || key}
              {param.min !== undefined && param.max !== undefined && (
                <span className="text-gray-500 ml-1">
                  ({param.min}-{param.max})
                </span>
              )}
            </label>
            {param.min !== undefined && param.max !== undefined ? (
              <div className="space-y-1">
                <input
                  type="range"
                  min={param.min}
                  max={param.max}
                  value={value}
                  onChange={(e) => handleSettingChange(key, parseInt(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{param.min}</span>
                  <span className="font-medium">{value}</span>
                  <span>{param.max}</span>
                </div>
              </div>
            ) : (
              <input
                type="number"
                value={value}
                onChange={(e) => handleSettingChange(key, parseInt(e.target.value))}
                className="w-full p-2 border rounded-md"
                min={param.min}
                max={param.max}
              />
            )}
          </div>
        );

      case 'string':
        if (param.options && Array.isArray(param.options)) {
          return (
            <div key={key} className="space-y-2">
              <label className="block text-sm font-medium">
                {param.description || key}
              </label>
              <select
                value={value}
                onChange={(e) => handleSettingChange(key, e.target.value)}
                className="w-full p-2 border rounded-md"
              >
                {param.options.map((option: string) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          );
        } else {
          return (
            <div key={key} className="space-y-2">
              <label className="block text-sm font-medium">
                {param.description || key}
              </label>
              <input
                type="text"
                value={value}
                onChange={(e) => handleSettingChange(key, e.target.value)}
                className="w-full p-2 border rounded-md"
              />
            </div>
          );
        }

      case 'boolean':
        return (
          <div key={key} className="space-y-2">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={value}
                onChange={(e) => handleSettingChange(key, e.target.checked)}
                className="rounded"
              />
              <span className="text-sm font-medium">
                {param.description || key}
              </span>
            </label>
          </div>
        );

      default:
        return (
          <div key={key} className="space-y-2">
            <label className="block text-sm font-medium">
              {param.description || key}
            </label>
            <input
              type="text"
              value={value}
              onChange={(e) => handleSettingChange(key, e.target.value)}
              className="w-full p-2 border rounded-md"
            />
          </div>
        );
    }
  };

  const parameterEntries = Object.entries(processor.parameters);

  if (parameterEntries.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            处理器设置
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500 text-center py-4">
            该处理器没有可配置的参数
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="w-5 h-5" />
          处理器设置
        </CardTitle>
        <CardDescription>
          配置 {processor.name} 的处理参数
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {parameterEntries.map(([key, param]) => renderParameterInput(key, param))}
        </div>
      </CardContent>
    </Card>
  );
};